import { EASE_OUT } from './easing';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Transport for the reel, and only ever in theatre.
 *
 * The docked player is a thumbnail you click to open — a scrub bar across it
 * would be four pixels tall and impossible to hit, so it deliberately has none.
 * Expanded, the reel is being watched rather than glanced at, and someone
 * showing work to a client needs to hold on a frame and run back over a moment.
 *
 * Kept off the picture: a hairline along the bottom edge that thickens under
 * the pointer, inside the gradient the theatre already has, and gone again
 * after a few seconds of stillness. The work is the subject; this is furniture.
 */

interface ReelScrubberProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** theatre only */
  active: boolean;
  /** The whole theatre's chrome shares one clock, kept by the player. */
  visible: boolean;
  /** `hold` pins it open for the length of a drag. */
  onActivity: (hold?: boolean) => void;
  /** On a wide screen the hero lays its caption over the bottom of the frame —
   *  job title, reel year — so the row is raised clear of it there. On a phone
   *  that copy sits below the card in the page flow, nothing overlaps, and the
   *  row belongs on the edge like everywhere else. */
  raised: boolean;
  /** The sound control, rendered as the last item of the row so pause, line and
   *  sound centre as one group rather than pinning to opposite edges. */
  trailing?: React.ReactNode;
  /** Owned by the player: pausing the element from here alone gets undone by
   *  MuxVideo's next canplay retry, which is what made this button look dead. */
  onToggle: () => void;
}

/** Only ever spoken now — the visible numbers were noise on a 68-second reel,
 *  but a slider still has to say where it is. */
const clock = (s: number) => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

const ReelScrubber: React.FC<ReelScrubberProps> = ({ videoRef, active, visible, onToggle, onActivity, raised, trailing }) => {
  const [playing, setPlaying] = useState(true);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrubbingRef = useRef(false);
  scrubbingRef.current = scrubbing;

  const wake = useCallback(() => onActivity(), [onActivity]);

  // rAF rather than timeupdate: browsers fire that about four times a second,
  // which is visible as a stutter on a bar this wide.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !active) return;
    let raf = 0;
    const read = () => {
      setTime(v.currentTime);
      if (v.duration && Number.isFinite(v.duration)) setDuration(v.duration);
      setPlaying(!v.paused);
      raf = requestAnimationFrame(read);
    };
    raf = requestAnimationFrame(read);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, active]);

  const toggle = useCallback(() => {
    wake();
    // Always from a real gesture, so this never spends the autoplay allowance
    // that iOS grants the element once and never gives back.
    onToggle();
  }, [onToggle, wake]);

  const seekToClientX = useCallback(
    (clientX: number) => {
      const v = videoRef.current;
      const el = trackRef.current;
      if (!v || !el || !v.duration || !Number.isFinite(v.duration)) return;
      const r = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      v.currentTime = ratio * v.duration;
      setTime(v.currentTime);
    },
    [videoRef]
  );

  // One pointer path for mouse, pen and touch. touch-action: none on the track
  // stops the browser claiming the drag for a scroll halfway through.
  const onTrackDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setScrubbing(true);
    onActivity(true); // pinned: a drag that pauses to aim must not fade out
    seekToClientX(e.clientX);
  };
  const onTrackMove = (e: React.PointerEvent) => {
    if (!scrubbingRef.current) return;
    e.stopPropagation();
    seekToClientX(e.clientX);
  };
  const onTrackUp = (e: React.PointerEvent) => {
    if (!scrubbingRef.current) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    setScrubbing(false);
    onActivity(false);
  };

  // Space and the arrows, because a reel gets watched at a keyboard as often as
  // on a phone. Escape already belongs to the theatre itself.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      const v = videoRef.current;
      if (!v) return;
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        toggle();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        wake();
        const step = e.shiftKey ? 1 : 5;
        v.currentTime = Math.min(v.duration || 0, Math.max(0, v.currentTime + (e.key === 'ArrowRight' ? step : -step)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, toggle, videoRef, wake]);

  if (!active) return null;
  const pct = duration > 0 ? Math.min(100, (time / duration) * 100) : 0;
  const shown = visible || scrubbing;

  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          key="transport"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.32, ease: EASE_OUT }}
          onPointerMove={wake}
          onClick={(e) => e.stopPropagation()}
          // pointer-events-none here, auto on the row inside. This wrapper is
          // full-width for its gradient, so at z-20 it lay across the sound
          // control in the corner and swallowed the click — the button was
          // visible, enabled and unreachable. The cluster also outranks it now;
          // either fix alone would do, and a gradient should never be catching
          // clicks regardless.
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4 pt-10 md:px-7 ${
            raised ? 'pb-4 md:pb-20' : 'pb-4 md:pb-6'
          }`}
          style={{ background: 'linear-gradient(to top, rgba(6,7,12,0.75), transparent)' }}
        >
          <div className="pointer-events-auto flex items-center justify-center gap-4 md:gap-7">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              data-cursor={playing ? 'PAUSE' : 'PLAY'}
              aria-label={playing ? 'Pause showreel' : 'Play showreel'}
              // The site already has one control treatment. This is it, not a variant.
              className="shrink-0 h-12 w-12 md:h-14 md:w-14 flex items-center justify-center rounded-full backdrop-blur-md border border-white/30 bg-black/50 text-white transition-colors duration-300 hover:bg-white hover:text-ink"
            >
              {playing ? (
                <span className="flex gap-[3.5px]" aria-hidden="true">
                  <span className="block w-[3.5px] h-[15px] md:w-1 md:h-[17px] rounded-full bg-current" />
                  <span className="block w-[3.5px] h-[15px] md:w-1 md:h-[17px] rounded-full bg-current" />
                </span>
              ) : (
                <svg width="17" height="19" viewBox="0 0 13 15" aria-hidden="true" className="ml-[2px]">
                  <path d="M0 0.5 12.5 7.5 0 14.5Z" fill="currentColor" />
                </svg>
              )}
            </button>

            {/* The hit area is 24px tall so a thumb can find it; the line inside
                is 2px, or 5px once you are actually on it. */}
            <div
              ref={trackRef}
              role="slider"
              tabIndex={0}
              aria-label="Seek through the showreel"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration) || 0}
              aria-valuenow={Math.round(time)}
              aria-valuetext={`${clock(time)} of ${clock(duration)}`}
              onPointerDown={onTrackDown}
              onPointerMove={onTrackMove}
              onPointerUp={onTrackUp}
              onPointerCancel={onTrackUp}
              // Capped rather than spanning the frame: full width made the line
              // the loudest thing in the row on a wide screen, and 68 seconds
              // does not need a metre of travel to be scrubbable.
              className="group relative w-[150px] sm:w-[280px] md:w-[420px] h-7 flex items-center cursor-pointer touch-none select-none outline-none"
            >
              <div
                className={`relative w-full rounded-full bg-white/20 transition-[height] duration-200 ${
                  scrubbing ? 'h-[5px]' : 'h-[2px] group-hover:h-[5px] group-focus-visible:h-[5px]'
                }`}
              >
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-white"
                  style={{ width: `${pct}%` }}
                />
                <div
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.45)] transition-[width,height,opacity] duration-200 ${
                    scrubbing
                      ? 'w-3.5 h-3.5 opacity-100'
                      : 'w-0 h-0 opacity-0 group-hover:w-3 group-hover:h-3 group-hover:opacity-100 group-focus-visible:w-3 group-focus-visible:h-3 group-focus-visible:opacity-100'
                  }`}
                  style={{ left: `${pct}%` }}
                />
              </div>
            </div>

            {trailing}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReelScrubber;
